import React from 'react';

import type { HeaderMotionMedia } from '@shared/core/website/headerMotion/headerMotion.types';
import type { WebsiteHeaderPhotoWithCropInfo } from '@shared/types/WebsiteHeaderSettings.types';

import { useHeaderMotionConfig } from './useHeaderMotionConfig';

export type HeaderMotionProps = Readonly<{
  /** The header's saved `motion` blob, unvalidated. */
  motion: unknown;
  media: HeaderMotionMedia;
  /** Passed through to the type component; bump it to replay. Defaults to 0. */
  replayKey?: number;
  /** Passed through to the type component: the header's other photos, for multi-photo types (Showcase). */
  photos?: ReadonlyArray<WebsiteHeaderPhotoWithCropInfo>;
  children: React.ReactNode;
}>;

/**
 * Adds an optional Motion layer to a photo/video header.
 *
 * Off (flag off, motion missing/disabled/unknown, or media unsupported): renders `children` alone,
 * so the DOM is identical to not wrapping. On: renders `children` followed by the Motion type's
 * component as the next sibling. Never adds a wrapper element in either case — header layouts
 * style their media by position, and an extra element would break that.
 */
export const HeaderMotion = ({ motion, media, replayKey = 0, photos, children }: HeaderMotionProps) => {
  const resolved = useHeaderMotionConfig(motion, media);
  if (!resolved) {
    return <>{children}</>;
  }
  const { def, options } = resolved;
  return (
    <>
      {children}
      <def.Component
        options={options}
        media={media}
        replayKey={replayKey}
        photos={photos}
      />
    </>
  );
};
